import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import TopLoadingBar from '../TopLoadingBar';
import { Search, Bell, X, Menu } from 'lucide-react';
import { getUnreadNotificationCount } from '../../utils/api';
import hotelIcon from '../../assets/img/logo192.png';
import { useUserSettingsContext } from '../../context/UserSettingsContext';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import NotificationCenter from '../NotificationCenter/NotificationCenter';
import MfaGuardGate from '../MfaGuardGate';
import MobileTabBar from './MobileTabBar';
import { NATIVE_REPORTS_CATALOG, filterCatalogByReportGroups } from '../../config/nativeReportsCatalog';
import { useReportCatalogFlags } from '../../hooks/useReportCatalogFlags';

const MobileLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showNotificationCenter, setShowNotificationCenter] = useState(false);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { userSettings, currentOrganization } = useUserSettingsContext();
  const catalogFlags = useReportCatalogFlags();
  const notificationRef = useRef(null);

  // Close sidebar on navigation
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleSearch = (value) => {
    setSearchTerm(value);
    const needle = value.trim().toLowerCase();
    // Report-group grants (PBI permissions mapped server-side) gate search too.
    const nativeGroups = userSettings?.orgAccess?.[currentOrganization?.code]?.nativeReports;
    const catalog = filterCatalogByReportGroups(NATIVE_REPORTS_CATALOG, nativeGroups, catalogFlags);
    const results = needle
      ? catalog.flatMap((g) =>
          g.pages
            .filter((p) => p.title.toLowerCase().includes(needle) || g.group.toLowerCase().includes(needle))
            .map((p) => ({ id: p.key, type: 'page', name: p.title, reportName: g.group, path: p.path }))
        )
      : [];
    setSearchResults(results);
    setShowResults(results.length > 0);
  };

  // Load unread notification count
  const loadUnreadCount = async () => {
    if (userSettings?.email) {
      try {
        const count = await getUnreadNotificationCount(userSettings.email);
        setUnreadNotificationCount(count);
      } catch (error) {
        console.error('Error loading unread notification count:', error);
      }
    }
  };

  useEffect(() => {
    loadUnreadCount();
  }, [userSettings?.email]);

  const searchRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
        setShowSearch(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCloseSearch = () => {
    setShowSearch(false);
    setSearchTerm('');
    setSearchResults([]);
    setShowResults(false);
  };

  return (
    <div className="min-h-screen dark:bg-slate-900" style={{ background: 'var(--lodge-onyx-50)' }}>
      <MfaGuardGate />
      {/* Grid overlay — matches desktop */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgba(100, 116, 139, 0.07) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(100, 116, 139, 0.07) 1px, transparent 1px)
          `,
          backgroundSize: '32px 32px',
          zIndex: 0
        }}
      />
      <TopLoadingBar />

      {/* Top Bar */}
      <header className="fixed top-0 left-0 right-0 h-14 bg-white/95 backdrop-blur-xl border-b border-slate-200/60 dark:bg-slate-900/95 dark:border-slate-700/50 z-40 flex items-center px-3">
        {/* Left: Hamburger + Logo */}
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 -ml-1 rounded-lg active:bg-slate-100 dark:active:bg-slate-800 transition-colors"
        >
          <Menu size={22} className="text-slate-700 dark:text-slate-300" />
        </button>
        <div className="flex items-center gap-2 ml-1 min-w-0">
          <img src={hotelIcon} alt="Lodgeic" className="w-7 h-7 logo-themed shrink-0" />
          <div className="min-w-0 leading-tight">
            <span
              className="block text-[19px] text-slate-900 dark:text-slate-100"
              style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.02em' }}
            >
              lodgeic
            </span>
            {currentOrganization?.name ? (
              <span className="block text-[10px] uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400 truncate">
                {currentOrganization.name}
              </span>
            ) : null}
          </div>
        </div>

        {/* Right: Search & Notifications */}
        <div className="flex items-center gap-1 ml-auto">
          <button
            onClick={() => setShowSearch(!showSearch)}
            className="p-2 rounded-lg active:bg-slate-100 dark:active:bg-slate-800 transition-colors"
          >
            <Search size={20} className="text-slate-600 dark:text-slate-400" />
          </button>

          <div className="relative" ref={notificationRef}>
            <button
              onClick={() => {
                setShowNotificationCenter(!showNotificationCenter);
                if (!showNotificationCenter) {
                  loadUnreadCount();
                }
              }}
              className="p-2 rounded-lg active:bg-slate-100 dark:active:bg-slate-800 transition-colors relative"
            >
              <Bell size={20} className="text-slate-600 dark:text-slate-400" />
              {unreadNotificationCount > 0 && (
                <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-orange-500 text-white text-[10px] font-bold rounded-full">
                  {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                </span>
              )}
            </button>

            <NotificationCenter
              isOpen={showNotificationCenter}
              onClose={() => setShowNotificationCenter(false)}
              onNavigate={(path) => {
                navigate(path, { state: { forecastJump: Date.now() } });
                setShowNotificationCenter(false);
              }}
              onNotificationRead={loadUnreadCount}
            />
          </div>
        </div>
      </header>

      {/* Search Bar - Conditionally Shown Below Header */}
      {showSearch && (
        <div className="fixed top-14 left-0 right-0 z-30 bg-white/95 backdrop-blur-xl dark:bg-slate-900/95 border-b border-slate-200/60 dark:border-slate-700/50 p-3" ref={searchRef}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={() => setShowResults(searchTerm.trim() && searchResults.length > 0)}
              placeholder="Search reports and pages..."
              className="pl-10 pr-10 h-10 text-sm"
              autoFocus
            />
            <button
              onClick={handleCloseSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-md active:bg-slate-100 dark:active:bg-slate-700 transition-colors"
            >
              <X className="h-4 w-4 text-slate-400" />
            </button>
          </div>

          {/* Search Results */}
          {showResults && searchResults.length > 0 && (
            <div className="mt-2 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 max-h-64 overflow-y-auto">
              {searchResults.map((result) => (
                <button
                  key={result.id}
                  onClick={() => {
                    navigate(result.path);
                    handleCloseSearch();
                  }}
                  className="w-full text-left px-4 py-3 active:bg-slate-50 dark:active:bg-slate-800 border-b border-slate-100 dark:border-slate-700 last:border-b-0 transition-colors"
                >
                  <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{result.name}</div>
                  {result.type === 'page' && (
                    <div className="text-xs text-slate-500 dark:text-slate-400">in {result.reportName}</div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sidebar Overlay */}
      <div
        className={cn(
          "fixed inset-0 bg-black/50 z-50 transition-opacity duration-300",
          sidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar Drawer */}
      <div className={cn(
        "fixed inset-y-0 left-0 w-80 max-w-[85vw] z-50 transform transition-transform duration-300 ease-in-out bg-white dark:bg-slate-900",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <Sidebar isCollapsed={false} onToggle={() => setSidebarOpen(false)} isMobile={true} />
      </div>

      {/* Main Content — bottom padding clears the fixed tab bar */}
      <main
        className="mobile-main-content pt-16 min-h-screen px-3"
        style={{ overscrollBehavior: 'none', paddingBottom: 'calc(76px + env(safe-area-inset-bottom))' }}
      >
        <Outlet />
      </main>

      {/* Native-app-style bottom tabs (same routes as desktop) */}
      <MobileTabBar />
    </div>
  );
};

export default MobileLayout;
